import { Component, ElementRef, Input, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RequestService } from './service/request.service';
import { ContactMotivation, CreateContactRequest } from '../../models/requests/createContactRequest.model';
import { emailValidator, mustBeChecked, phoneValidator } from '../components/validators';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { DeviceUtils } from '../../utils/device-utils';
import { City } from '../../../model/city.model';
import { Country } from '../../../model/country.model';
import { District } from '../../../model/district.model';
import { Region } from '../../../model/region.model';
import { ViewStatus } from '@uranda/ui';
import { SearchService } from '../components/searchbar/search.service';
import { InsertOtpModalComponent } from '../insert-otp-modal/insert-otp-modal.component';
import { RequestMotivationEnum } from '../../models/requests/createLeadRequest.model';
import { NgxSpinnerService } from 'ngx-spinner';
import { ModalOffcanvasService } from '../../../services/modal-offcanvas.service';
import { OffcanvasHistoryService } from '../../../services/offcanvas-history.service';
import { SearchBarService } from '../../../services/utils/searchBar.service';
import { AlertNotificationsService } from '../../../services/alert-notifications.service';
import { isPhoneBelongsToClinicError } from '../../utils/clinic-phone-errors.util';

@Component({
  selector: 'app-request',
  templateUrl: './request.component.html',
  styleUrls: ['./request.component.scss'],
  animations: [
    trigger('horizontalStepTransition', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('500ms', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('500ms', style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false
})
export class RequestComponent implements OnInit, AfterViewInit {
  requestInfoFields: FormlyFieldConfig[] = [];
  specifyRequestField: FormlyFieldConfig[] = [];
  requestInfoForm = new FormGroup({});
  contactFields: FormlyFieldConfig[] = [];
  contactForm = new FormGroup({});
  formModel: any = {};
  scope?: string;
  country?: Country;
  district?: District;
  region?: Region;
  clinicName?: string;
  countdown = 60;
  partnerId = '';
  currentStep = 1;
  isMobile = DeviceUtils.checkDeviceType();
  countries: Country[] = [];
  viewStatus = ViewStatus.LOADING;
  protected readonly ViewStatus = ViewStatus;
  isSubmitting = false;
  /** Valorizzato quando il numero inserito ha già una richiesta di preventivo attiva. */
  activeRequestExpiresAt: string | null = null;
  accessLinkSent = false;
  /** Il numero inserito appartiene a una clinica: il backend rifiuta richieste fatte con esso. */
  phoneBelongsToClinic = false;

  success = false;
  @Input() isOffcanvas = false;
  /** Quando presente, il form è in modalità "completamento LeadCollector":
   *  usa PUT /v1/requests/quote/complete invece di POST /v1/requests/quote/create */
  @Input() lcAccessToken: string | null = null;
  acceptTermsAndConditions = false;
  isVideoPlaying = false;

  @ViewChild('dragHandle', { static: false }) dragHandle!: ElementRef;
  @ViewChild('guideVideo', { static: false }) guideVideo!: ElementRef<HTMLVideoElement>;


  constructor(
    protected translateService: TranslateService,
    private requestService: RequestService,
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected searchService: SearchService,
    protected modalService: NgbModal,
    protected searchbarService: SearchBarService,
    private offCanvasHistory: OffcanvasHistoryService,
    private spinner: NgxSpinnerService,
    protected modalOffcanvasService: ModalOffcanvasService,
    private alertNotifications: AlertNotificationsService
  ) { }

  getModalTitle(): string {
    if (this.isQuoteRequest()) {
      const locationParts = [
        this.district?.name,
        this.region?.name,
        this.country?.name,
      ].filter(Boolean);
      const location = locationParts.join(', ');
      return `${this.translateService.instant(
        'Quote request for'
      )} ${location}`;
    } else {
      return `${this.translateService.instant('Get in touch with')} ${this.clinicName
        }`;
    }
  }

  async ngOnInit(): Promise<void> {
    if (this.country == undefined) {
      this.country = this.searchbarService.allCountries?.[0]
    }
    this.initRequestInfoForm();
    this.initContactForm();
    this.viewStatus = ViewStatus.READY;
  }


  initRequestInfoForm() {
    const isPopup = this.scope === 'request-popup';

    this.requestInfoFields = [
      {
        key: 'requestMotivation',
        type: 'radio-description',
        props: {
          label: this.translateService.instant('What is your main need?'),
          required: true,
          compact: isPopup,
          options: [
            {
              label: this.translateService.instant('Dental care'),
              value: RequestMotivationEnum.DENTAL_CARE,
              description: this.translateService.instant(
                'Cavities, toothache, inflamed gums, cleaning, root canal treatments.'
              ),
            },
            {
              label: this.translateService.instant('Dental implants'),
              value: RequestMotivationEnum.DENTAL_IMPLANTS,
              description: this.translateService.instant(
                'To replace missing teeth with implants, bridges or dentures.'
              ),
            },
            {
              label: this.translateService.instant('Smile aesthetics'),
              value: RequestMotivationEnum.SMILE_AESTHETICS,
              description: this.translateService.instant(
                'Whitening, aligners, veneers, crowns, smile correction.'
              ),
            },
          ],
        },
      },

      {
        key: 'dentalArch',
        type: 'radio-description',
        props: {
          label: this.translateService.instant('Choose arch'),
          required: true,
          inline: !isPopup,
          compact: isPopup,
          options: [
            { label: this.translateService.instant('Upper'), value: 'UPPER' },
            { label: this.translateService.instant('Lower'), value: 'LOWER' },
            { label: this.translateService.instant('Both'), value: 'BOTH' },
          ],
        },
      },
      {
        key: 'treatmentType',
        type: 'radio-description',
        props: {
          label: this.translateService.instant('Select the desired treatment'),
          required: true,
          compact: isPopup,
          options: [
            {
              label: this.translateService.instant('Only what is necessary'),
              value: 'NECESSARY',
            },
            {
              label: this.translateService.instant('Total rehabilitation'),
              value: 'TOTAL',
            },
          ],
        },
      },

      {
        fieldGroup: [
          {
            key: 'attachmentsGroup',
            className: this.isMobile ? 'col-12 mb-3' : 'row',
            fieldGroup: [
              {
                className: 'col-12',
                key: 'panoramicAttachments',
                type: 'file',
                props: {
                  label: this.translateService.instant('Attach panoramic photo'),
                  multiple: true,
                  maxFiles: 2,
                },

              },
            ],
          },

          {
            className: this.isMobile ? 'col-12' : 'row',
            fieldGroup: [
              {
                className: 'col-12',
                key: 'othersAttachments',
                type: 'file',
                props: {
                  label: this.translateService.instant('Attach other photos'),
                  required: false,
                  maxFiles: 5,
                  multiple: true,
                },
              },
            ],
          },
        ],
      },
    ];

    this.specifyRequestField = [
      {
        key: 'requestDetails',
        type: 'textarea',
        className: 'fw-semibold',

        props: {
          label: this.translateService.instant('Specify your request'),
          placeholder: this.translateService.instant(
            'Please note that it is not permitted to share Phone numbers, email, etc.'
          ),
          required: false,
          rows: 5,
          maxLength: 500,
        },

        hooks: {
          onInit: (field) => {
            const control = field.formControl;
            const attachmentsControl = this.requestInfoForm.get(
              'attachmentsGroup.panoramicAttachments'
            );

            if (control && (control.value === null || control.value === undefined)) {
              control.setValue('', { emitEvent: false });
            }

            const attachmentsVal = attachmentsControl?.value as any;
            const hasAttachmentsInitial =
              (Array.isArray(attachmentsVal) && attachmentsVal.length > 0) ||
              (typeof attachmentsVal === 'string' && attachmentsVal.length > 0);

            if (hasAttachmentsInitial) {
              control?.setValidators([Validators.maxLength(500)]);
            } else {
              control?.setValidators([Validators.minLength(30), Validators.maxLength(500)]);
            }
            control?.updateValueAndValidity({ emitEvent: false });

            const currentDetails = (control?.value || '') as string;
            // If requestDetails has at least one character, panoramicAttachments are NOT required
            const hasAnyCharInitial = currentDetails.length >= 1;
            if (attachmentsControl) {
              attachmentsControl.setValidators(hasAnyCharInitial ? [] : [Validators.required]);
              attachmentsControl.updateValueAndValidity({ emitEvent: false });
            }

            attachmentsControl?.valueChanges.subscribe((value) => {
              const val = value as any;
              const hasAttachments =
                (Array.isArray(val) && val.length > 0) ||
                (typeof val === 'string' && val.length > 0);

              if (hasAttachments) {
                control?.setValidators([Validators.maxLength(500)]);
              } else {
                control?.setValidators([Validators.minLength(30), Validators.maxLength(500)]);
              }

              control?.updateValueAndValidity({ emitEvent: false });
            });

            control?.valueChanges.subscribe((value: string) => {
              const hasAnyChar = (value || '').length >= 1;

              if (attachmentsControl) {
                attachmentsControl.setValidators(hasAnyChar ? [] : [Validators.required]);
                attachmentsControl.updateValueAndValidity({ emitEvent: false });
              }
            });
          },
        },

        expressionProperties: {
          'props.description': (_model, _formState, field) => {
            const currentLength = field?.formControl?.value?.length || 0;
            return (
              `${currentLength} / ${field?.props?.maxLength} ` +
              this.translateService.instant('characters entered')
            );
          },
        },
      }

    ];
  }


  requestNewQuote() {
    this.formModel = {};
    this.currentStep = 1;
  }

  initContactForm() {
    const isPopup = this.scope === 'request-popup';
    const rowClass = isPopup ? 'd-flex flex-wrap gap-2' : 'row';
    const colClass = isPopup ? 'flex-grow-1' : 'col-12 col-md-6';
    const isPrefilled = !!this.lcAccessToken;

    const motivationField = {
      key: 'motivation',
      type: 'radio-description',
      props: {
        label: this.translateService.instant('Request motivation'),
        required: true,
        inline: true,
        compact: isPopup,
        options: [
          {
            label: this.translateService.instant('Informations'),
            value: ContactMotivation.INFO,
          },
          {
            label: this.translateService.instant('Appointment'),
            value: ContactMotivation.APPOINTMENT,
          },
          {
            label: this.translateService.instant('Quote'),
            value: ContactMotivation.QUOTE,
          },
        ],
      },
    };

    this.contactFields = [
      ...(this.scope === 'contact' ? [motivationField] : []),
      {
        fieldGroupClassName: rowClass,
        fieldGroup: [
          {
            className: colClass,
            key: 'firstname',
            type: 'input',
            props: {
              label: 'Firstname',
              required: true,
              minLength: 2,
              disabled: isPrefilled,
            },
          },
          {
            className: colClass,
            key: 'lastname',
            type: 'input',
            props: {
              label: 'Lastname',
              required: true,
              minLength: 2,
              disabled: isPrefilled,
            },
          },
        ],
      },
      {
        fieldGroupClassName: rowClass,
        fieldGroup: [
          {
            className: 'col-12 col-md-4',
            key: 'location',
            type: 'country-autocomplete',
            props: {
              label: this.translateService.instant('Country *'),
              required: true,
            },
          },
          {
            className: 'col-12 col-md-4',
            key: 'phoneNumber',
            wrappers: ['form-field'],
            type: 'phone',
            props: {
              showLabel: false,
              label: this.translateService.instant('Phone number'),
              required: true,
              appendToBody: true,
              disabled: isPrefilled,
              linkCountryField: 'location',
            },
            validators: {
              phone: phoneValidator,
            },
            hooks: {
              onInit: (field) => {
                if (field?.formControl) {
                  const formControl = field.formControl;
                  formControl.valueChanges.subscribe(() => {
                    // Il blocco mostrato si riferisce al numero precedente: azzeralo subito,
                    // altrimenti resta visibile mentre l'utente corregge il numero.
                    this.activeRequestExpiresAt = null;
                    this.accessLinkSent = false;
                    this.phoneBelongsToClinic = false;
                  });

                  // Il pre-check di rete è rate-limited lato backend (60/h per IP): non deve
                  // partire ad ogni keystroke, ma solo quando il numero si è "assestato".
                  formControl.valueChanges
                    .pipe(debounceTime(450), distinctUntilChanged())
                    .subscribe(() => {
                      // La deduplica riguarda solo le richieste di preventivo (il backend non la
                      // applica a /contact/create) e, nel flusso LeadCollector, il numero è già
                      // verificato da /lc/verify prima che il form venga compilato: interrogare qui
                      // bloccherebbe il paziente sulla propria stessa richiesta.
                      if (this.scope !== 'contact' && !this.lcAccessToken && formControl.valid) {
                        void this.checkActiveRequest();
                      }
                    });
                }
              },
            },
          },
          {
            className: 'col-12 col-md-4',
            key: 'email',
            type: 'input',
            props: {
              label: this.translateService.instant('Email'),
              required: false,
              disabled: isPrefilled,
            },
            validators: {
              email: emailValidator,
            },
          },
          {
            className: 'col-12 col-md-12 mt-2',
            key: 'termsAndConditions',
            type: 'checkbox',
            defaultValue: false,
            props: {
              label: this.translateService.instant(
                'I have read and accept the Terms and Conditions and Privacy Policy'
              ),
              required: true,
            },
            hooks: {
              onInit: (field) => {
                if (field?.formControl) {
                  field.formControl.valueChanges.subscribe((value) => {
                    this.acceptTermsAndConditions = value;
                  });
                }
              },
            },
            validators: {
              mustBeChecked: mustBeChecked,
            },
          },
        ],
      },
    ];
  }

  isNextDisabled(): boolean {
    return this.requestInfoForm.invalid
  }


  goToNextStep() {
    if (this.isNextDisabled()) {
      this.ensureRequestDetailsMinLengthValidation();
      this.requestInfoForm.markAllAsTouched();
      return;
    }
    if (this.currentStep < 2) {
      this.currentStep++;

      if (this.isOffcanvas) {
        setTimeout(() => {
          const offcanvasContent = document.querySelector(
            '.offcanvas .offcanvas-content'
          ) as HTMLElement;
          if (offcanvasContent) {
            offcanvasContent.scrollTop = 0;
          }
        }, 0);
      }
    }
  }

  goToPreviousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }

    if (this.isOffcanvas) {
      setTimeout(() => {
        const offcanvasContent = document.querySelector(
          '.offcanvas .offcanvas-content'
        ) as HTMLElement;
        if (offcanvasContent) {
          offcanvasContent.scrollTop = 0;
        }
      }, 0);
    }
  }

  isQuoteRequest() {
    return this.scope && this.scope == 'quote';
  }

  /**
   * Interroga il backend appena il numero è completo, così l'utente scopre il blocco prima di
   * compilare il resto e prima che parta l'OTP, non dopo aver premuto Confirm.
   * Non blocca mai il form in caso di errore di rete: il backend rifiuta comunque alla creazione.
   */
  async checkActiveRequest(): Promise<boolean> {
    const phone = this.formModel.phoneNumber as string | undefined;
    if (!phone) {
      this.activeRequestExpiresAt = null;
      return false;
    }

    try {
      const status = await this.requestService.getActiveQuoteRequest(phone);
      // Il numero potrebbe essere cambiato mentre la richiesta era in volo: una risposta
      // arrivata in ordine sparso (es. Ra dopo Rb) non deve sovrascrivere lo stato di un
      // numero diverso da quello attualmente in campo.
      const clinicNumber = !!status.clinicNumber;
      if (this.formModel.phoneNumber !== phone) {
        return status.blocked || clinicNumber;
      }
      // Il numero di una clinica prevale: la richiesta verrebbe rifiutata comunque.
      this.phoneBelongsToClinic = clinicNumber;
      this.activeRequestExpiresAt = status.blocked && !clinicNumber ? status.expiresAt : null;
      this.accessLinkSent = false;
      return status.blocked || clinicNumber;
    } catch (error) {
      if (this.formModel.phoneNumber !== phone) {
        return false;
      }
      console.error(error);
      this.activeRequestExpiresAt = null;
      return false;
    }
  }

  async onResendAccessLink(): Promise<void> {
    const phone = this.formModel.phoneNumber as string | undefined;
    if (!phone) {
      return;
    }
    try {
      await this.requestService.resendAccessLink(phone);
      // Stessa guardia di checkActiveRequest: ignora l'esito se il numero è cambiato
      // mentre la richiesta era in volo.
      if (this.formModel.phoneNumber !== phone) {
        return;
      }
      this.accessLinkSent = true;
    } catch (error) {
      if (this.formModel.phoneNumber !== phone) {
        return;
      }
      console.error(error);
      this.alertNotifications.error(
        this.translateService.instant('errors.genericRetry')
      );
    }
  }

  async onConfirm() {
    if (!this.canSubmit()) {
      if (this.currentStep === 1) {
        this.ensureRequestDetailsMinLengthValidation();
      }
      this.contactForm.markAllAsTouched();
      return;
    }
    this.spinner.show();
    this.isSubmitting = true;

    try {
      // Rete di sicurezza: il check al blur può essere andato a vuoto per un errore di rete,
      // o la richiesta può essere stata verificata nel frattempo da un altro device.
      // Nel flusso LeadCollector (lcAccessToken) il numero è già verificato prima del form: questo
      // pre-check bloccherebbe il paziente sulla propria stessa richiesta. Il guard reale per quel
      // flusso è PUT /quote/complete lato backend, che esclude correttamente il lead in corso.
      if (this.scope !== 'contact' && !this.lcAccessToken && await this.checkActiveRequest()) {
        return;
      }

      if (this.scope == 'contact') {
        await this.createContactRequest();
      } else {
        await this.createQuoteRequest();
      }
      this.success = true;
    } catch (error: any) {
      // L'interceptor sopprime i toast per v1/requests, quindi senza questo ramo il 409
      // non produrrebbe alcun feedback e il form resterebbe fermo senza spiegazione.
      if (isPhoneBelongsToClinicError(error)) {
        // 422 dedicato (contatto e preventivo): messaggio tradotto sotto al campo telefono.
        this.phoneBelongsToClinic = true;
        this.activeRequestExpiresAt = null;
      } else if (error?.status === 409) {
        this.activeRequestExpiresAt = error?.error?.data?.expiresAt ?? null;
        this.accessLinkSent = false;
      } else {
        console.error(error);
        this.alertNotifications.error(
          this.translateService.instant('errors.genericRetry')
        );
      }
    } finally {
      this.spinner.hide();
      this.isSubmitting = false;
    }
  }

  ensureRequestDetailsMinLengthValidation() {
    const detailsControl = this.requestInfoForm.get('requestDetails');
    const attachmentsControl = this.requestInfoForm.get('attachmentsGroup.panoramicAttachments');

    const detailsVal = (detailsControl?.value || '') as string;
    const attachmentsVal = (attachmentsControl?.value) as any;
    const hasAttachments =
      (Array.isArray(attachmentsVal) && attachmentsVal.length > 0) ||
      (typeof attachmentsVal === 'string' && attachmentsVal.length > 0);

    if (!hasAttachments && detailsVal.length < 30) {
      detailsControl?.setErrors({ minlength: { requiredLength: 30, actualLength: detailsVal.length } });
    } else if (detailsControl?.errors) {
      const errs = { ...detailsControl.errors } as any;
      if (errs.minlength) {
        delete errs.minlength;
        if (Object.keys(errs).length === 0) {
          detailsControl.setErrors(null);
        } else {
          detailsControl.setErrors(errs);
        }
      }
    }
  }

  async createContactRequest() {
    await this.requestService.createContactRequest({
      partnerId: this.partnerId,
      firstname: this.formModel.firstname,
      lastname: this.formModel.lastname,
      country: JSON.stringify(this.formModel.location),
      address: this.formModel.address,
      city: this.formModel.city,
      phoneNumber: this.formModel.phoneNumber,
      email: this.formModel.email,
      motivation: this.formModel.motivation,
    } as CreateContactRequest);
  }

  async createQuoteRequest() {
    const pano = this.formModel?.attachmentsGroup?.panoramicAttachments;
    if (this.lcAccessToken) {
      // Flusso LeadCollector: completa la QuoteRequest PENDING esistente
      await this.requestService.completeQuoteRequest(
        this.lcAccessToken,
        pano?.[0],
        pano?.[1],
        this.formModel.othersAttachments,
        this.formModel,
        this.country?.id,
        this.region?.id,
        this.district?.id
      );
    } else {
      await this.requestService.createOfferRequest(
        this.country?.id,
        this.region?.id,
        this.district?.id,
        pano?.[0],
        pano?.[1],
        this.formModel.othersAttachments,
        this.formModel
      );
    }
  }

  closeOffcanvas() {
    this.offCanvasHistory.dismiss();
  }

  canSubmit(): boolean {
    if (!this.acceptTermsAndConditions) {
      return false;
    }
    // La deduplica riguarda solo le richieste di preventivo: il backend non applica alcun guard
    // su POST /contact/create, quindi bloccare qui il "Contatta la clinica" sarebbe un falso
    // positivo silenzioso senza alcuna spiegazione per l'utente.
    if (this.scope !== 'contact' && this.activeRequestExpiresAt) {
      return false;
    }
    // Vale per tutti gli scope: il backend rifiuta sia /contact/create sia /quote/create.
    if (this.phoneBelongsToClinic) {
      return false;
    }
    return !this.isSubmitting && this.contactForm.valid;
  }

  ngAfterViewInit() {
    if (this.guideVideo?.nativeElement) {
      const video = this.guideVideo.nativeElement;
      video.addEventListener('play', () => {
        this.isVideoPlaying = true;
      });
      video.addEventListener('pause', () => {
        this.isVideoPlaying = false;
      });
      video.addEventListener('ended', () => {
        this.isVideoPlaying = false;
      });
    }
  }

  playVideo() {
    if (this.guideVideo?.nativeElement) {
      this.guideVideo.nativeElement.play();
      this.isVideoPlaying = true;
    }
  }

  getGuideVideoUrl(): string {
    // Restituisce l'URL del video guida in base allo step corrente e al tipo di form
    if (this.isQuoteRequest()) {
      return this.currentStep === 1
        ? '/assets/guide/quote-step1.mp4'
        : '/assets/guide/quote-step2.mp4';
    } else {
      return '/assets/guide/contact-form.mp4';
    }
  }

  getGuideText(): string {
    if (this.isQuoteRequest()) {
      return this.currentStep === 1
        ? 'guide.quoteStep1'
        : 'guide.quoteStep2';
    } else {
      return 'guide.contactForm';
    }
  }
}
